import {
  buildPrintDocument, buildSamplePrescriptionData, createPrintTranslator, getPrescriptionViaNames,
  renderPrescriptionVia, renderOptionsFromLayout, resolvePrintLayout, buildPageCounterLabels, renderTermBody,
  SAMPLE_HEADER_TOKENS, SAMPLE_TERM_HTML, SAMPLE_TERM_TITLE,
  type HeaderTokenValues, type PrescriptionDocumentData, type RawPrintSettings, type RenderPrescriptionOptions,
} from "@vet/print-core";
import type { PrintSettings } from "@/hooks/usePrintSettings";

export type PreviewVariant = "simples" | "controle" | "digital" | "manipulada" | "exame";
type Lang = "pt-BR" | "en";

const labels = (lang: Lang) => ({ lang, headerAlt: lang === "pt-BR" ? "Cabeçalho" : "Header", footerAlt: lang === "pt-BR" ? "Rodapé" : "Footer" });
const fontBaseUrl = () => (typeof window !== "undefined" ? window.location.origin : null);

export function buildPrescriptionPreviewHtml({ printSettings, data, lang, signed, viaIndex = 0, zones = false, headerTokens, pageLabel, digitalSeal, draftHighlight }: {
  printSettings: Partial<PrintSettings> | null; data: PrescriptionDocumentData; lang: Lang; signed: boolean;
  viaIndex?: number; zones?: boolean; headerTokens?: HeaderTokenValues; pageLabel?: string | null;
  digitalSeal?: { detail: string; instruction: string; validationUrl: string; qrLabel: string } | null;
  draftHighlight?: RenderPrescriptionOptions["draftHighlight"];
}): string {
  const raw = (printSettings ?? {}) as RawPrintSettings;
  const t = createPrintTranslator(lang);
  const opts = { ...renderOptionsFromLayout(resolvePrintLayout(raw, "prescription"), signed), draftHighlight: draftHighlight ?? null };
  const viaNames = getPrescriptionViaNames(data, t);
  const label = pageLabel ?? buildPageCounterLabels({ viaNames, pagesPerPart: viaNames.map(() => 1), trailingPages: signed ? 1 : 0, t })[viaIndex];
  return buildPrintDocument({
    content: renderPrescriptionVia(data, viaIndex, t, lang, opts), printSettings: raw, kind: "prescription",
    labels: labels(lang), headerTokens, fontBaseUrl: fontBaseUrl(), reserveSealSpace: signed,
    preview: { pageLabel: label, zones, digitalSeal: signed ? digitalSeal : null },
  });
}

export function buildTermPreviewHtml({ printSettings, title, html, lang, zones = false }: {
  printSettings: Partial<PrintSettings> | null; title: string; html: string; lang: Lang; zones?: boolean;
}): string {
  const t = createPrintTranslator(lang);
  return buildPrintDocument({
    title, content: renderTermBody(html), printSettings: (printSettings ?? {}) as RawPrintSettings,
    kind: "term", labels: labels(lang), fontBaseUrl: fontBaseUrl(),
    preview: { pageLabel: t("print.vias.pageNofM", { n: 1, total: 1 }), zones },
  });
}

export function buildSamplePreviewHtml({ printSettings, scope, variant, lang, zones = false }: {
  printSettings: Partial<PrintSettings> | null; scope: "prescription" | "term"; variant: PreviewVariant; lang: Lang; zones?: boolean;
}): string {
  if (scope === "term") {
    return buildTermPreviewHtml({ printSettings, title: SAMPLE_TERM_TITLE[lang], html: SAMPLE_TERM_HTML[lang], lang, zones });
  }
  const dataVariant = variant === "digital" ? "simples" : variant;
  const t = createPrintTranslator(lang);
  return buildPrescriptionPreviewHtml({
    printSettings, data: buildSamplePrescriptionData(dataVariant), lang, signed: variant === "digital",
    zones, headerTokens: SAMPLE_HEADER_TOKENS,
    digitalSeal: lang === "pt-BR"
      ? {
          detail: t("print.digitalSeal.sampleDetails", { vet: "Dra. Marina Costa Ribeiro", crmv: "CRMV-PR 12345", owner: "Carlos Eduardo Lima", clinic: "Clínica Veterinária Bem-Estar" }),
          instruction: t("print.digitalSeal.scanInstruction"),
          validationUrl: "https://validar.iti.gov.br",
          qrLabel: t("print.digitalSeal.qrDemo"),
        }
      : {
          detail: t("print.digitalSeal.sampleDetails", { vet: "Dr. Marina Costa Ribeiro", crmv: "CRMV-PR 12345", owner: "Carlos Eduardo Lima", clinic: "Clínica Veterinária Bem-Estar" }),
          instruction: t("print.digitalSeal.scanInstruction"),
          validationUrl: "https://validar.iti.gov.br",
          qrLabel: t("print.digitalSeal.qrDemo"),
        },
  });
}
